import React, { useCallback, useEffect, useState } from "react";
import { useIdleClose } from "../hooks/useIdleClose";
import { X, Mail, Loader2, Check, AlertTriangle, Trash2, Database, BarChart3, RotateCcw } from "lucide-react";
import { authFetch, useAuth, isPlatformAdmin } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import DatabaseDashboardModal from "./DatabaseDashboardModal";

const API = process.env.REACT_APP_BACKEND_URL;

// Step 7.6 (2026-02-15) — Platform Admin "Settings" modal.
//
// Reachable only from the Admin dropdown when the current user is a
// Platform Administrator. Renders every entry from
// `/api/admin/platform-settings` (source of truth for min/max/label/
// default) so a future backend-side knob addition auto-appears here
// without a frontend change. Values are validated on submit against
// the same `meta` block the backend enforces.
//
// Layout mirrors the SOP + Change-Password modals: 8-px shell, 3-px
// controls, coral inline error, navy "Save Changes" primary + slate
// "Cancel" secondary. Backdrop click closes; Save is disabled until at
// least one field is dirty AND passes client-side validation.

export default function PlatformSettingsModal({ open, onClose }) {
  const { refresh: refreshPublicSettings } = useSettings();
  const { user } = useAuth();
  const showSandboxSection = isPlatformAdmin(user);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [meta, setMeta] = useState({});
  // 2026-03-06 (Dave) — Database health panel moved from the Admin
  // tab (which lost its "Database" card) into this modal as an
  // inline card. `dbOpen` tracks whether the read-only glance panel
  // is currently overlaid on top of the settings modal.
  const [dbOpen, setDbOpen] = useState(false);
  const [values, setValues] = useState({});
  const [dirty, setDirty] = useState({});
  // Chart Settings pop-up (own Save/Cancel; snapshot restores on Cancel).
  const [chartOpen, setChartOpen] = useState(false);
  const [chartSnap, setChartSnap] = useState({});
  const [chartSaving, setChartSaving] = useState(false);
  const [chartError, setChartError] = useState("");
  const [chartConfirm, setChartConfirm] = useState(false);

  // 2026-08-18 (Dave, Postmark baby step 2) — inline "Send test email"
  // control that hits POST /api/admin/platform-settings/test-email. All
  // state is local to the modal so it never survives a close/re-open.
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null); // {ok, dry_run, error, to}
  const [testRecipient, setTestRecipient] = useState("");

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetch(`${API}/api/admin/platform-settings`);
      if (!res.ok) throw new Error(`Load failed (${res.status})`);
      const data = await res.json();
      setMeta(data.meta || {});
      setValues(data.settings || {});
      setDirty({});
    } catch (e) {
      setError(e?.message || "Could not load Platform Settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadAll();
  }, [open, loadAll]);

  useIdleClose(open, onClose);

  if (!open) return null;

  const sendTestEmail = async () => {
    setTestSending(true);
    setTestResult(null);
    try {
      const body = testRecipient.trim() ? { to: testRecipient.trim() } : {};
      const res = await authFetch(`${API}/api/admin/platform-settings/test-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      setTestResult({
        ok: !!data.ok && res.ok,
        dry_run: !!data.dry_run,
        to: data.to,
        error: data.ok ? null : (data.error || `HTTP ${res.status}`),
      });
    } catch (e) {
      setTestResult({ ok: false, error: e?.message || "Network error" });
    } finally {
      setTestSending(false);
    }
  };

  const handleChange = (key, raw) => {
    setValues((prev) => ({ ...prev, [key]: raw }));
    setDirty((prev) => ({ ...prev, [key]: true }));
  };

  const validateLocal = () => {
    for (const key of Object.keys(dirty)) {
      const m = meta[key];
      if (!m) continue;
      const n = Number(values[key]);
      if (!Number.isInteger(n)) return `${m.label} must be an integer`;
      if (n < m.min || n > m.max)
        return `${m.label} must be between ${m.min} and ${m.max}`;
    }
    return "";
  };

  const handleSave = async () => {
    const localErr = validateLocal();
    if (localErr) {
      setError(localErr);
      return;
    }
    const changed = {};
    for (const key of Object.keys(dirty)) {
      changed[key] = Number(values[key]);
    }
    if (Object.keys(changed).length === 0) {
      onClose?.();
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await authFetch(`${API}/api/admin/platform-settings`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ values: changed }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Save failed (${res.status})`);
      }
      const fresh = await res.json();
      setValues(fresh);
      setDirty({});
      await refreshPublicSettings();
      onClose?.();
    } catch (e) {
      setError(e?.message || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  // 2026-03-08 (Dave) — Password Policy pulled out of the auto-rendered
  // grid so it can be stacked in a single grid cell with Email Service
  // (see renderGroupCard + the stacked wrapper below). Keeps the modal
  // to a single screen with no vertical scroll.
  const groupOrder = [
    { title: "Sessions", keys: ["session_idle_minutes", "session_expiring_warning_seconds", "session_max_hours_ephemeral", "session_ceiling_hours", "idle_dashboard_snapback_seconds"] },
    { title: "New Associations", keys: ["new_association_seed_element_count", "new_assoc_target_pct", "new_assoc_inflation_rate", "new_assoc_interest_rate", "new_assoc_year1_adjustment_pct", "new_assoc_opening_reserve_balance"] },
    // 2026-02-28 (Dave) — Payment Settings card.  Starts with the
    // one master switch that decides whether anonymous visitors see
    // the marketing landing (+ enrollment form) or drop straight to
    // LoginScreen.  Cluster more payment-provider knobs here later.
  ];
  const paymentGroup = { title: "Payment Settings", keys: ["web_enrollment_enabled", "enrollment_demo_mode", "monthly_fee_usd", "pro_fee_usd", "pilot_fee_usd"] };
  const chartBreakGroup = { title: "Main Chart", keys: ["dashboard_chart_height_pct", "chart_break_first_enabled", "chart_break_trigger_pct", "chart_break_second_enabled", "chart_break_second_trigger_pct", "chart_break_headroom_pct", "chart_break_position_pct"] };
  const dashSpacingGroup = { title: "Dashboard Spacing", keys: ["dashboard_panel_gap_px", "dashboard_chart_gap_px", "dashboard_edge_side_px", "dashboard_edge_tb_px"] };
  const chartRevealsGroup = { title: "Chart Reveals", keys: ["per_member_reveal_seconds", "hover_reveal_seconds", "hover_visible_seconds"] };
  const chartKeys = [...chartRevealsGroup.keys, ...chartBreakGroup.keys, ...dashSpacingGroup.keys];

  const openChart = () => {
    setChartSnap(Object.fromEntries(chartKeys.map((k) => [k, values[k]])));
    setChartError("");
    setChartConfirm(false);
    setChartOpen(true);
  };
  const dropChartDirty = () =>
    setDirty((prev) => {
      const next = { ...prev };
      chartKeys.forEach((k) => delete next[k]);
      return next;
    });
  const cancelChart = () => {
    setValues((prev) => ({ ...prev, ...chartSnap }));
    dropChartDirty();
    setChartOpen(false);
  };
  const saveChart = async (override) => {
    const changed = {};
    if (override) Object.assign(changed, override);
    for (const k of override ? [] : chartKeys) {
      if (!dirty[k]) continue;
      const m = meta[k];
      const n = Number(values[k]);
      if (m && (!Number.isInteger(n) || n < m.min || n > m.max)) {
        setChartError(`${m.label} must be a whole number between ${m.min} and ${m.max}`);
        return;
      }
      changed[k] = n;
    }
    if (Object.keys(changed).length === 0) {
      setChartOpen(false);
      return;
    }
    setChartSaving(true);
    setChartError("");
    try {
      const res = await authFetch(`${API}/api/admin/platform-settings`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ values: changed }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Save failed (${res.status})`);
      }
      const fresh = await res.json();
      setValues((prev) => ({ ...prev, ...Object.fromEntries(chartKeys.map((k) => [k, fresh[k]])) }));
      dropChartDirty();
      await refreshPublicSettings();
      setChartOpen(false);
      setChartConfirm(false);
    } catch (e) {
      setChartError(e?.message || "Could not save chart settings");
    } finally {
      setChartSaving(false);
    }
  };
  const passwordPolicyGroup = { title: "Password Policy", keys: ["password_min_length", "password_require_uppercase", "password_require_number", "password_require_special"] };

  // Step 7.7 · a setting with min=0 / max=1 renders as a two-state toggle
  // instead of a number input. Cleaner UX for the three password
  // complexity flags.
  const isBoolKey = (m) => m && m.min === 0 && m.max === 1;

  const saveDisabled = saving || loading || Object.keys(dirty).length === 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{
        backgroundColor: "rgba(15, 42, 68, 0.55)",
        backdropFilter: "blur(1.5px)",
        WebkitBackdropFilter: "blur(1.5px)",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose?.();
      }}
      data-testid="platform-settings-backdrop"
    >
      <div
        className="bg-white shadow-2xl border-2 border-navy w-[min(1200px,96vw)] h-[min(940px,98vh)] flex flex-col rounded-[8px]"
        data-testid="platform-settings-modal"
      >
        {/* Modal header. 2026-02-15 (Dave, revision 8) — the rule under
            the title is placed on an `mx-6` inner wrapper that shares
            the same 24 px horizontal inset as the modal body below, so
            it renders exactly as wide as the section-heading coral
            rules (Sessions / Chart Reveals / …). Colour changed coral
            → navy so it visually anchors the title as a distinct
            hierarchy level while the coral section rules retain their
            "topic marker" role. */}
        <div className="pt-2 pb-1">
          <div
            className="mx-6 flex items-center justify-between gap-3 pb-1.5"
            style={{ borderBottom: "2px solid #1C4E80" }}
          >
            <div className="flex items-baseline gap-3">
              <div className="text-[15px] font-bold text-navy" data-testid="platform-settings-title">
                Platform Settings
              </div>
              <div className="text-[11.5px] text-slate leading-snug">
                Tune sessions, chart demo timings, new-association seeding, and password policy. Changes apply to every user immediately.
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-slate hover:text-navy rounded-[3px] p-1 shrink-0"
              data-testid="platform-settings-close"
              disabled={saving}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* 2026-10-01 (Dave) — overflow-y stays `auto` so smaller
            viewports can still scroll if content ever overflows, but
            we hide the scrollbar visuals entirely (WebKit + Firefox)
            because at normal desktop viewports the content fits and
            an always-on scrollbar track reads as visual noise. */}
        <div className="px-6 py-2 overflow-y-auto flex-1 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
          {loading ? (
            /* 2026-02-27 (Dave) — centered so the modal opens at its
               final full size (fixed shell height above) and the load
               indicator sits neatly mid-body instead of clinging to the
               top-left and making the modal look half-rendered. */
            <div className="flex items-center justify-center h-full text-[11.5px] text-slate italic">
              Loading…
            </div>
          ) : (
            /* 2026-02-27 (Dave) — 6-card grid so every section (4
               settings groups + Postmark test + Sandbox Cleanup) is
               visible without vertical scrolling. Default `stretch`
               alignment keeps every card in a row locked to the tallest
               sibling — top-row trio and bottom-row trio each align on
               a clean baseline.
               Tightened 2026-02-27 (Dave, 2nd pass): grid gap 3 → 2,
               card padding p-3 → p-2.5, row space-y 2.5 → 2 so the
               modal collapses onto a single screen with no vertical
               scroll on 1080 p viewports. */
            /* 2026-03-08 (Dave) — Grid restructure to eliminate vertical
               scroll. Password Policy + Email Service stack in one grid
               cell; Version Content spans 2 columns with its Tabs and
               Dashboard Widgets side-by-side internally; Sandbox
               Cleanup spans 2 columns to accommodate its longer form.
               `auto-rows-min` prevents rows from stretching to match a
               single tall neighbour. */
            (() => {
              const renderGroupCard = ({ title, keys }) => (
                <div
                  key={title}
                  className="border border-rule rounded-[6px] p-2 bg-white"
                  data-testid={`platform-settings-card-${title.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <div
                    className="text-[10.5px] tracking-[0.12em] uppercase font-bold text-navy mb-1 pb-1"
                    style={{ borderBottom: "1px solid #EA6A47" }}
                  >
                    {title}
                  </div>
                  <div className="space-y-0.5">
                    {keys.map((key) => {
                      const m = meta[key];
                      if (!m) return null;
                      const isBool = isBoolKey(m);
                      const curVal = Number(values[key] ?? 0);
                      const displayVal = values[key] ?? "";
                      const displayMin = m.min;
                      const displayMax = m.max;
                      return (
                        <div key={key} className="grid grid-cols-[1fr_100px] items-center gap-3">
                          <label
                            className="text-[12px] text-ink leading-tight"
                            htmlFor={`ps-${key}`}
                          >
                            {m.label}
                            {/* 2026-03-09 (Dave) — Suppress the "(minimum · maximum)"
                                hint for plain-dollar inputs. Those fields render as
                                money inputs, not sliders; the caption would imply a
                                % constraint that doesn't apply. */}
                            {!isBool && key !== "new_assoc_opening_reserve_balance" && !["monthly_fee_usd", "pro_fee_usd", "pilot_fee_usd"].includes(key) && (
                              <span className="block text-slate text-[10.5px] mt-0.5">
                                ({chartKeys.includes(key) && meta[key]?.default != null ? `standard ${meta[key].default} · ` : ""}minimum {displayMin} · maximum {displayMax})
                              </span>
                            )}
                          </label>
                          {isBool ? (
                            <button
                              type="button"
                              id={`ps-${key}`}
                              role="switch"
                              aria-checked={curVal === 1}
                              onClick={() => handleChange(key, curVal === 1 ? 0 : 1)}
                              disabled={saving}
                              data-testid={`platform-settings-${key}`}
                              className="justify-self-end inline-flex items-center gap-2 select-none"
                              style={{ cursor: saving ? "not-allowed" : "pointer" }}
                            >
                              <span
                                className="inline-block rounded-full transition-colors"
                                style={{
                                  width: 32,
                                  height: 18,
                                  backgroundColor: curVal === 1 ? "#1C4E80" : "#B4BEC7",
                                  position: "relative",
                                }}
                              >
                                <span
                                  className="inline-block bg-white rounded-full absolute top-0.5 transition-all"
                                  style={{
                                    width: 14,
                                    height: 14,
                                    left: curVal === 1 ? 16 : 2,
                                  }}
                                />
                              </span>
                              <span className="text-[11px] font-semibold text-ink w-6 text-right">
                                {curVal === 1 ? "On" : "Off"}
                              </span>
                            </button>
                          ) : (
                            <input
                              id={`ps-${key}`}
                              type="number"
                              value={displayVal}
                              min={displayMin}
                              max={displayMax}
                              step={1}
                              onChange={(e) => handleChange(key, e.target.value)}
                              className="border border-rule px-2 py-1 text-[13px] font-mono tabular-nums text-right focus:outline-none focus:border-navy rounded-[3px]"
                              data-testid={`platform-settings-${key}`}
                              disabled={saving}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
              return (
            <div className="grid grid-cols-3 gap-1.5 auto-rows-min">
              {renderGroupCard(groupOrder[0])}
              <div
                className="border border-rule rounded-[6px] p-2 bg-white"
                data-testid="platform-settings-card-chart-settings"
              >
                <div
                  className="text-[10.5px] tracking-[0.12em] uppercase font-bold text-navy mb-1.5 pb-1"
                  style={{ borderBottom: "1px solid #EA6A47" }}
                >
                  Dashboard Settings
                </div>
                <div className="text-[11.5px] text-ink leading-snug mb-2">
                  Main chart height, panel spacing, column breaks, and chart reveal timings.
                </div>
                <button
                  type="button"
                  onClick={openChart}
                  className="self-start inline-flex items-center gap-1.5 px-3 py-1.5 border border-navy text-navy text-[12px] font-semibold rounded-[3px] hover:bg-navy hover:text-white transition-colors"
                  data-testid="platform-settings-open-chart-settings"
                >
                  <BarChart3 size={12} />
                  Dashboard Settings
                </button>
              </div>
              {groupOrder.slice(1).map((g) => renderGroupCard(g))}

              {chartOpen && (
                <div
                  className="fixed inset-0 z-[60] flex items-center justify-center px-4"
                  style={{ backgroundColor: "rgba(15, 42, 68, 0.45)" }}
                  onClick={(e) => { if (e.target === e.currentTarget && !chartSaving) cancelChart(); }}
                  data-testid="chart-settings-backdrop"
                >
                  <div
                    className="bg-white shadow-2xl border-2 border-navy w-[min(820px,96vw)] flex flex-col rounded-[8px]"
                    data-testid="chart-settings-modal"
                  >
                    <div className="mx-6 pt-3 pb-1.5 flex items-center justify-between" style={{ borderBottom: "2px solid #1C4E80" }}>
                      <div className="text-[15px] font-bold text-navy" data-testid="chart-settings-title">Dashboard Settings</div>
                      <button type="button" onClick={cancelChart} disabled={chartSaving} className="text-slate hover:text-navy rounded-[3px] p-1" data-testid="chart-settings-close">
                        <X size={16} />
                      </button>
                    </div>
                    <div className="px-6 py-3 grid grid-cols-2 gap-1.5 items-start">
                      {renderGroupCard(chartBreakGroup)}
                      <div className="grid gap-1.5" data-testid="chart-settings-right-stack">
                        {renderGroupCard(dashSpacingGroup)}
                        {renderGroupCard(chartRevealsGroup)}
                      </div>
                    </div>
                    <div className="px-6 pb-3 flex items-center justify-end gap-2">
                      {chartError && (
                        <div className="mr-auto text-[12px] text-coral" data-testid="chart-settings-error">{chartError}</div>
                      )}
                      <div className={"flex items-center gap-2" + (chartError ? "" : " mr-auto")} data-testid="chart-settings-reset-area">
                        {chartConfirm ? (
                          <>
                            <span className="text-[12px] text-ink">Reset all dashboard settings to standard?</span>
                            <button
                              type="button"
                              onClick={() => saveChart(Object.fromEntries(chartKeys.filter((k) => meta[k]).map((k) => [k, meta[k].default])))}
                              disabled={chartSaving}
                              className="px-3 py-1.5 border border-coral text-coral text-[12px] font-semibold rounded-[3px] hover:bg-coral hover:text-white transition-colors"
                              data-testid="chart-settings-reset-confirm"
                            >
                              {chartSaving ? "Resetting…" : "Yes, reset"}
                            </button>
                            <button type="button" onClick={() => setChartConfirm(false)} disabled={chartSaving} className="text-slate hover:text-navy text-[12px] font-semibold px-2" data-testid="chart-settings-reset-no">
                              No
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setChartConfirm(true)}
                            disabled={chartSaving}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-navy text-navy text-[12px] font-semibold rounded-[3px] hover:bg-navy hover:text-white transition-colors"
                            data-testid="chart-settings-reset"
                          >
                            <RotateCcw size={12} />
                            Reset to Standard
                          </button>
                        )}
                      </div>
                      <button type="button" onClick={cancelChart} disabled={chartSaving} className="text-slate hover:text-navy text-[13px] font-semibold px-4 py-2 rounded-[3px]" data-testid="chart-settings-cancel">
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => saveChart()}
                        disabled={chartSaving || !chartKeys.some((k) => dirty[k])}
                        className="text-white text-[13px] font-semibold px-5 py-2 rounded-[3px] transition-colors"
                        style={{ backgroundColor: chartSaving || !chartKeys.some((k) => dirty[k]) ? "#B4BEC7" : "#1C4E80" }}
                        data-testid="chart-settings-save"
                      >
                        {chartSaving ? "Saving…" : "Save"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Rows 2–3: three stacked cells share subgrid rows so each card row is one height. */}
              <div className="col-span-3 grid grid-cols-3 gap-1.5" style={{ gridTemplateRows: "auto auto" }} data-testid="platform-settings-rows-2-3">
              <div className="row-span-2 grid" data-testid="platform-settings-cell-payment-chart">
                {renderGroupCard(paymentGroup)}
              </div>

              {/* 2026-03-08 (Dave) — Password Policy + Email Service
                  stacked in a single grid cell so their combined
                  height matches the tall Version Content card next
                  to them. Copy in Email Service tightened to a
                  single line to keep the stack short. */}
              <div className="row-span-2 grid gap-1.5" style={{ gridTemplateRows: "subgrid" }} data-testid="platform-settings-cell-pwd-email">
                {renderGroupCard(passwordPolicyGroup)}
                <div
                  className="border border-rule rounded-[6px] p-2 bg-white"
                  data-testid="platform-settings-card-email-postmark"
                >
                  <div
                    className="text-[10.5px] tracking-[0.12em] uppercase font-bold text-navy mb-2 pb-1"
                    style={{ borderBottom: "1px solid #EA6A47" }}
                  >
                    Email Service
                  </div>
                  <div className="text-[11.5px] text-ink leading-snug mb-2">
                    Send a test through the app&apos;s mail service to verify the sender token.
                  </div>
                  <div className="flex flex-col gap-2">
                    <input
                      type="email"
                      value={testRecipient}
                      onChange={(e) => setTestRecipient(e.target.value)}
                      disabled={testSending}
                      placeholder="Recipient (optional)"
                      className="border border-rule px-2 py-1.5 text-[12px] rounded-[3px] focus:outline-none focus:border-navy w-full"
                      data-testid="platform-settings-test-email-to"
                    />
                    <button
                      type="button"
                      onClick={sendTestEmail}
                      disabled={testSending}
                      className="self-start inline-flex items-center gap-1.5 px-3 py-1.5 border border-navy text-navy text-[12px] font-semibold rounded-[3px] hover:bg-navy hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      data-testid="platform-settings-test-email"
                    >
                      {testSending ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
                      {testSending ? "Sending…" : "Send test email"}
                    </button>
                    {testResult && (
                      <div
                        className={
                          "inline-flex items-start gap-1.5 px-2 py-1 rounded-[3px] text-[11px] leading-snug " +
                          (testResult.ok
                            ? "text-[#1F7A3A] bg-[#EBF6EE] border border-[#B7DFC4]"
                            : "text-coral bg-[#FEF5F2] border border-coral")
                        }
                        style={{ maxWidth: "100%" }}
                        data-testid="platform-settings-test-email-result"
                      >
                        {testResult.ok ? <Check size={11} className="mt-0.5 shrink-0" /> : <AlertTriangle size={11} className="mt-0.5 shrink-0" />}
                        <span>
                          {testResult.ok
                            ? (testResult.dry_run
                                ? "Dry-run: send skipped (POSTMARK_ENABLED=false)."
                                : `Sent to ${testResult.to} — check that inbox.`)
                            : `Failed: ${testResult.error}`}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 2026-10-01 (Dave) — Column-3 stack: Database card
                  (short, Password-Policy-height) above System Testing
                  Cleanup (Email-Service-height).  Mirrors the Pwd+Email
                  flex-col in column 2 so top-row columns 2 and 3 each
                  carry two stacked cards and read evenly.  Version
                  Content moves to row 2, col-span-3 full-width. */}
              {showSandboxSection && (
                <div
                  className="row-span-2 grid gap-1.5"
                  style={{ gridTemplateRows: "subgrid" }}
                  data-testid="platform-settings-cell-db-cleanup"
                >
                  <div
                    className="border border-rule rounded-[6px] p-2 bg-white"
                    data-testid="platform-settings-card-database"
                  >
                    <div
                      className="text-[10.5px] tracking-[0.12em] uppercase font-bold text-navy mb-1.5 pb-1"
                      style={{ borderBottom: "1px solid #EA6A47" }}
                    >
                      Database
                    </div>
                    <div className="text-[11.5px] text-ink leading-snug mb-2">
                      Read-only health: DB size, row counts, live sessions, email status, migration head.
                    </div>
                    <button
                      type="button"
                      onClick={() => setDbOpen(true)}
                      className="self-start inline-flex items-center gap-1.5 px-3 py-1.5 border border-navy text-navy text-[12px] font-semibold rounded-[3px] hover:bg-navy hover:text-white transition-colors"
                      data-testid="platform-settings-open-database"
                    >
                      <Database size={12} />
                      Open Database Dashboard
                    </button>
                  </div>
                  <div
                    className="border border-rule rounded-[6px] p-2 bg-white"
                    data-testid="platform-settings-card-sandbox-cleanup"
                  >
                    <SandboxCleanupSection />
                  </div>
                </div>
              )}
              </div>

              {(() => {
                const tabRows = [
                  { label: "Contributions Tab", basicKey: "contributions_tab_basic", proKey: "contributions_tab_pro" },
                  { label: "Elements Tab",      basicKey: "elements_tab_basic",      proKey: "elements_tab_pro"      },
                  { label: "Projections Tab",   basicKey: "projections_tab_basic",   proKey: "projections_tab_pro"   },
                  { label: "Illustrations Tab", basicKey: "illustrations_tab_basic", proKey: "illustrations_tab_pro" },
                ];
                const widgetRows = [
                  { label: "Elements Cost Bars",     basicKey: "dash_elements_cost_basic", proKey: "dash_elements_cost_pro" },
                  { label: "Waterfall Chart",        basicKey: "dash_waterfall_basic",     proKey: "dash_waterfall_pro"     },
                  { label: "Funding Mix Donut",      basicKey: "dash_funding_mix_basic",   proKey: "dash_funding_mix_pro"   },
                  { label: "Funding Level Gauge",    basicKey: "dash_funding_gauge_basic", proKey: "dash_funding_gauge_pro" },
                  { label: "Reserve Activity Chart", basicKey: "dash_activity_basic",      proKey: "dash_activity_pro"      },
                ];
                const renderBox = (key) => {
                  const m = meta[key];
                  if (!m) return null;
                  const cur = Number(values[key] ?? 0);
                  return (
                    <label
                      className="inline-flex items-center justify-center cursor-pointer"
                      style={{ cursor: saving ? "not-allowed" : "pointer" }}
                    >
                      <input
                        type="checkbox"
                        checked={cur === 1}
                        onChange={() => handleChange(key, cur === 1 ? 0 : 1)}
                        disabled={saving}
                        /* 2026-10-01 (Dave) — Pale sky-blue accent
                           instead of navy; keeps the check visually
                           present but much softer than royal-blue. */
                        style={{ width: 16, height: 16, accentColor: "#8EC9E8" }}
                        data-testid={`platform-settings-${key}`}
                        aria-label={m.label}
                      />
                    </label>
                  );
                };
                const renderVersionInnerColumn = (heading, rows) => (
                  <div>
                    <div className="grid grid-cols-[1fr_44px_44px] items-center gap-x-2 gap-y-0.5">
                      <div className="text-[10px] tracking-[0.10em] uppercase font-bold text-slate">
                        {heading}
                      </div>
                      <div className="text-[10px] tracking-[0.10em] uppercase font-bold text-slate text-center">Basic</div>
                      <div className="text-[10px] tracking-[0.10em] uppercase font-bold text-slate text-center">Pro</div>
                      {rows.map((row) => (
                        <React.Fragment key={row.label}>
                          <div className="text-[12px] text-ink leading-tight">{row.label}</div>
                          <div className="flex justify-center">{renderBox(row.basicKey)}</div>
                          <div className="flex justify-center">{renderBox(row.proKey)}</div>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                );
                return (
                  <div
                    className="border border-rule rounded-[6px] p-2 bg-white col-span-3"
                    data-testid="platform-settings-card-version-content"
                  >
                    <div
                      className="flex items-baseline justify-between gap-3 mb-1 pb-1"
                      style={{ borderBottom: "1px solid #EA6A47" }}
                    >
                      <div className="text-[10.5px] tracking-[0.12em] uppercase font-bold text-navy">
                        Version Content
                      </div>
                      <div className="text-[10.5px] text-slate italic leading-snug">
                        Check the versions where each tab should appear at startup.
                      </div>
                    </div>
                    {/* 2026-10-01 (Dave) — 4-column grid so every Tabs
                        row and every Dashboard-Widgets row shares one
                        baseline, with the merged "Dashboard Widgets"
                        heading spanning the 3 right-hand widget pairs.
                        Tightened gap-y-0.5 to collapse the checkbox
                        rows vertically. */}
                    <div className="grid grid-cols-4 gap-x-5">
                      {renderVersionInnerColumn("Tabs", tabRows)}
                      {renderVersionInnerColumn("Dashboard Widgets", widgetRows.slice(0, 3))}
                      {renderVersionInnerColumn("\u00A0", widgetRows.slice(3))}
                    </div>
                  </div>
                );
              })()}
            </div>
              );
            })()
          )}
        </div>

        {error && (
          <div
            className="mx-6 mb-3 px-2.5 py-2 border border-coral bg-[#FEF5F2] text-coral text-[11px] leading-snug rounded-[3px]"
            data-testid="platform-settings-error"
          >
            {error}
          </div>
        )}

        <div
          className="px-6 py-2 flex items-center justify-end gap-2"
          style={{ borderTop: "1px solid #E4E7EB" }}
        >
          <button
            type="button"
            onClick={onClose}
            className="text-[12px] font-semibold px-4 py-2 rounded-[3px] text-slate hover:text-navy transition-colors"
            data-testid="platform-settings-cancel"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saveDisabled}
            className="text-white text-[13px] font-semibold px-5 py-2 rounded-[3px] transition-colors"
            style={{
              backgroundColor: saveDisabled ? "#B4BEC7" : "#1C4E80",
              cursor: saveDisabled ? "not-allowed" : "pointer",
            }}
            data-testid="platform-settings-save"
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
      {/* 2026-03-06 (Dave) — Database Dashboard overlay. Sits on
          top of the Platform Settings modal (higher z-index) so
          Platform Admins can peek at DB health without leaving
          Database Settings. */}
      {dbOpen && <DatabaseDashboardModal onClose={() => setDbOpen(false)} />}
    </div>
  );
}



// ─── SandboxCleanupSection ──────────────────────────────────────────────
// Platform-Admin-only control that lists every synthetic test row in the
// database (firms / associations / users whose name or email starts with
// PYTEST, TEST, or SANDBOX — case-insensitive) and offers a typing-to-
// confirm hard-delete. Deletes cascade through ORM relationships so plan
// years, elements, scenarios, memberships, etc. are all cleared in one
// atomic transaction on the backend.
function SandboxCleanupSection() {
  const [preview, setPreview] = useState(null);   // {firms, associations, users, counts, prefixes}
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);     // {deleted:{firms,assoc,users}} on success
  const [showLists, setShowLists] = useState(false);

  const loadPreview = useCallback(async () => {
    setLoading(true); setErr(""); setResult(null);
    try {
      const res = await authFetch(`${API}/api/admin/sandbox-cleanup/preview`);
      if (!res.ok) throw new Error(`Preview failed (${res.status})`);
      setPreview(await res.json());
    } catch (e) {
      setErr(e?.message || "Could not load preview.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const totalCount = preview ? (preview.counts.firms + preview.counts.associations + preview.counts.users) : 0;

  const runCleanup = async () => {
    setBusy(true); setErr("");
    try {
      const res = await authFetch(`${API}/api/admin/sandbox-cleanup/execute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirm: "DELETE" }),
      });
      if (!res.ok) {
        let msg = `Cleanup failed (${res.status})`;
        try { const j = await res.json(); if (j?.detail) msg = j.detail; } catch { /* ignore */ }
        throw new Error(msg);
      }
      const data = await res.json();
      setResult(data);
      setConfirmText("");
      // Refresh the preview so the user sees "Nothing to clean up".
      await loadPreview();
    } catch (e) {
      setErr(e?.message || "Cleanup failed.");
    } finally {
      setBusy(false);
    }
  };

  const canDelete = !busy && totalCount > 0 && confirmText === "DELETE";

  return (
    <div className="mb-1" data-testid="sandbox-cleanup-section">
      <div
        className="text-[10.5px] tracking-[0.12em] uppercase font-bold text-navy mb-2 pb-1"
        style={{ borderBottom: "1px solid #EA6A47" }}
      >
        System Testing Cleanup
      </div>
      <div className="text-[11.5px] text-ink leading-snug mb-2">
        Deletes records whose name or email starts with{" "}
        <code className="px-1 py-0.5 bg-canvas rounded-[3px] text-[10.5px] font-mono text-navy">PYTEST</code>,{" "}
        <code className="px-1 py-0.5 bg-canvas rounded-[3px] text-[10.5px] font-mono text-navy">TEST</code>, or{" "}
        <code className="px-1 py-0.5 bg-canvas rounded-[3px] text-[10.5px] font-mono text-navy">SANDBOX</code>. Fixtures flagged{" "}
        <code className="px-1 py-0.5 bg-canvas rounded-[3px] text-[10.5px] font-mono text-navy">is_test_user</code>{" "}
        are protected.
      </div>

      {loading && (
        <div className="flex items-center gap-1.5 text-[11px] text-slate italic">
          <Loader2 size={11} className="animate-spin" /> Loading preview…
        </div>
      )}

      {!loading && preview && (
        <>
          {totalCount === 0 ? (
            <div
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-[3px] text-[11.5px] text-[#1F7A3A] bg-[#EBF6EE] border border-[#B7DFC4]"
              data-testid="sandbox-cleanup-empty"
            >
              <Check size={11} /> Nothing to clean up.
            </div>
          ) : (
            <>
              <div
                className="flex items-baseline gap-3 flex-wrap mb-2"
                data-testid="sandbox-cleanup-counts"
              >
                <CountPill label="Firms" n={preview.counts.firms} />
                <CountPill label="Associations" n={preview.counts.associations} />
                <CountPill label="Users" n={preview.counts.users} />
                <button
                  type="button"
                  onClick={() => setShowLists((v) => !v)}
                  className="text-[11px] text-navy underline underline-offset-2 hover:text-coral"
                  data-testid="sandbox-cleanup-toggle-lists"
                >
                  {showLists ? "Hide list" : "Show list"}
                </button>
              </div>

              {showLists && (
                /* 2026-02-27 (Dave) — Tighter cap so revealing a long
                   list (dozens of PYTEST rows after a heavy CI run)
                   scrolls INSIDE this list block instead of stretching
                   the card taller than its sibling cards in the
                   Automated System Testing Cleanup row. */
                <div
                  className="mb-3 max-h-[120px] overflow-y-auto border border-rule rounded-[3px] p-2 text-[11px] leading-snug bg-canvas"
                  data-testid="sandbox-cleanup-lists"
                >
                  <ListGroup title="Firms" rows={preview.firms.map((f) => f.name)} />
                  <ListGroup title="Associations" rows={preview.associations.map((a) => a.name)} />
                  <ListGroup title="Users" rows={preview.users.map((u) => `${u.full_name} · ${u.email}`)} />
                </div>
              )}

              <div className="flex items-center gap-2 flex-wrap">
                <label className="text-[11px] text-slate leading-tight">
                  Type <code className="px-1 py-0.5 bg-canvas rounded-[3px] text-[10.5px] font-mono text-coral">DELETE</code> to confirm:
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  disabled={busy}
                  autoComplete="off"
                  spellCheck={false}
                  className="border border-rule px-2 py-1 text-[12px] font-mono rounded-[3px] focus:outline-none focus:border-coral w-[140px]"
                  data-testid="sandbox-cleanup-confirm-input"
                />
                <button
                  type="button"
                  onClick={runCleanup}
                  disabled={!canDelete}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-[3px] transition-colors"
                  style={{
                    backgroundColor: canDelete ? "#EA6A47" : "#B4BEC7",
                    color: "#fff",
                    cursor: canDelete ? "pointer" : "not-allowed",
                  }}
                  data-testid="sandbox-cleanup-execute"
                >
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                  {busy ? "Deleting…" : "Delete Permanently"}
                </button>
              </div>
            </>
          )}
        </>
      )}

      {result && (
        <div
          className="inline-flex items-start gap-1.5 px-2 py-1 mt-2 rounded-[3px] text-[11px] leading-snug text-[#1F7A3A] bg-[#EBF6EE] border border-[#B7DFC4]"
          data-testid="sandbox-cleanup-result"
        >
          <Check size={11} className="mt-0.5 shrink-0" />
          <span>
            Deleted {result.deleted.firms} firm(s), {result.deleted.associations} association(s),
            and {result.deleted.users} user(s).
          </span>
        </div>
      )}

      {err && (
        <div
          className="inline-flex items-start gap-1.5 px-2 py-1 mt-2 rounded-[3px] text-[11px] leading-snug text-coral bg-[#FEF5F2] border border-coral"
          data-testid="sandbox-cleanup-error"
        >
          <AlertTriangle size={11} className="mt-0.5 shrink-0" />
          <span>{err}</span>
        </div>
      )}
    </div>
  );
}

function CountPill({ label, n }) {
  return (
    <span className="inline-flex items-baseline gap-1 text-[11.5px]">
      <span className="font-bold text-navy tabular-nums" data-testid={`sandbox-cleanup-count-${label.toLowerCase()}`}>{n}</span>
      <span className="text-slate">{label}</span>
    </span>
  );
}

function ListGroup({ title, rows }) {
  if (!rows.length) return null;
  return (
    <div className="mb-2 last:mb-0">
      <div className="text-[9.5px] tracking-[0.1em] uppercase font-semibold text-slate mb-0.5">
        {title} ({rows.length})
      </div>
      <ul className="pl-3 list-disc marker:text-slate">
        {rows.map((r, i) => (
          <li key={i} className="text-ink truncate" title={r}>{r}</li>
        ))}
      </ul>
    </div>
  );
}
